import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLogStream } from '../../hooks/useLogStream';

interface Props {
  serviceId: string | null;
  serviceName: string;
  unit: string | null;
  onClose: () => void;
}

const DRAWER_WIDTH = 640;

function lineColor(line: string): string | undefined {
  const u = line.toUpperCase();
  if (u.includes('ERROR') || u.includes('CRIT') || u.includes('EMERG') || u.includes('ALERT')) {
    return '#ef5350';
  }
  if (u.includes('WARN') || u.includes('WARNING')) {
    return '#ffa726';
  }
  return undefined;
}

export function LogDrawer({ serviceId, serviceName, unit, onClose }: Props) {
  const { lines, connected } = useLogStream(unit);
  const [autoScroll, setAutoScroll] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines, autoScroll]);

  const open = serviceId !== null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: DRAWER_WIDTH } } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5, minHeight: 56 }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
          {serviceName}
        </Typography>
        <Chip
          size="small"
          label={connected ? 'live' : 'offline'}
          color={connected ? 'success' : 'default'}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Auto-scroll</Typography>}
          sx={{ mr: 0 }}
        />
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          bgcolor: '#0d0d0d',
          px: 2,
          py: 1,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          lineHeight: 1.6,
        }}
      >
        {lines.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {connected ? 'Waiting for log lines…' : 'Connecting…'}
          </Typography>
        )}
        {lines.map((line, i) => {
          const color = lineColor(line);
          return (
            <Box
              key={i}
              component="div"
              sx={{ color: color ?? 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {line}
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>
    </Drawer>
  );
}
